import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaApple, FaAndroid, FaWindows, FaDownload, FaCheckCircle, FaRocket, FaShieldAlt, FaUsers, FaGlobe, FaBrain, FaRobot, FaVideo, FaExclamationTriangle } from 'react-icons/fa';
import { SiMacos } from 'react-icons/si';
import './DownloadPage.css';
import IOSGuide from '../components/IOSGuide';
import MacGuide from '../components/MacGuide';
import WeChatTip from '../components/WeChatTip';
import { isBot } from '../utils/detectBot';

const DownloadPage = () => {
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [showMacGuide, setShowMacGuide] = useState(false);
  const [showWeChatTip, setShowWeChatTip] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredModel, setHoveredModel] = useState(null);
  const [showDownloads, setShowDownloads] = useState(false);

  // 检测是否为爬虫，如果不是则显示下载模块
  useEffect(() => {
    const botDetected = isBot();
    if (!botDetected) {
      // 真实用户，延迟显示下载模块（避免爬虫通过首屏抓取）
      const timer = setTimeout(() => {
        setShowDownloads(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // 检测微信浏览器
  const isWeChatBrowser = () => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.indexOf('micromessenger') !== -1;
  };

  // AI 模型列表
  const aiModels = [
    {
      id: 1,
      name: 'GPT-5.2 Pro',
      icon: <FaBrain />,
      color: 'from-orange-500 to-amber-500',
      description: '最强大的语言模型',
      features: ['多模态理解', '128K上下文', '实时推理']
    },
    {
      id: 2,
      name: 'Gemini 3 Pro',
      icon: <FaRobot />,
      color: 'from-green-500 to-emerald-500',
      description: 'Google最新AI',
      features: ['多语言精通', '视觉理解', '代码生成']
    },
    {
      id: 3,
      name: 'Nano Banana Pro',
      icon: <FaRocket />,
      color: 'from-yellow-400 to-orange-500',
      description: '超快响应速度',
      features: ['顶尖绘图', '不限使用', '低延迟']
    },
    {
      id: 4,
      name: 'Claude Sonnet 4.5',
      icon: <FaBrain />,
      color: 'from-amber-500 to-rose-500',
      description: 'Anthropic旗舰模型',
      features: ['长文本处理', '精准分析', '代码专家']
    },
    {
      id: 5,
      name: 'Grok-4.1',
      icon: <FaRobot />,
      color: 'from-orange-400 to-red-500',
      description: 'xAI创新模型',
      features: ['实时数据', '幽默对话', '深度思考']
    },
    {
      id: 6,
      name: 'Sora-2',
      icon: <FaVideo />,
      color: 'from-purple-500 to-pink-500',
      description: 'AI视频生成',
      features: ['视频创作', '高清输出', '场景理解']
    },
    {
      id: 7,
      name: 'Veo-3.1',
      icon: <FaVideo />,
      color: 'from-blue-500 to-cyan-500',
      description: 'Google视频AI',
      features: ['专业级画质', '长视频', '精准控制']
    },
    {
      id: 8,
      name: 'DeepSeek-V3.1',
      icon: <FaBrain />,
      color: 'from-green-400 to-teal-500',
      description: '中国领先AI',
      features: ['中文优化', '数学推理', '超快回复']
    }
  ];

  const platforms = [
    {
      id: 'ios',
      name: 'iOS',
      icon: <FaApple />,
      description: '支持 iPhone 和 iPad，随时随地智能对话',
      downloadUrl: '/gptpro.mobileconfig',
      version: '1.2.0',
      size: '12 KB',
      updateDate: '2024-12-26',
      color: 'from-orange-500 to-amber-500',
      features: ['语音对话', '云端同步', '快速响应'],
      disabled: false,
      disabledText: ''
    },
    {
      id: 'android',
      name: 'Android',
      icon: <FaAndroid />,
      description: '安卓全平台支持，高级语音通话功能',
      downloadUrl: '#',
      version: '1.0.0',
      size: '21.8 MB',
      updateDate: '2024-11-28',
      color: 'from-green-500 to-emerald-500',
      features: ['多模态交互', '离线模式', '智能助手'],
      disabled: true,
      disabledText: '正在更新'
    },
    {
      id: 'windows',
      name: 'Windows',
      icon: <FaWindows />,
      description: '强大的桌面客户端，支持全部高级功能',
      downloadUrl: 'https://juliawl.oss-cn-shanghai.aliyuncs.com/ai/down/ChatGPT-Setup-1.0.1.exe',
      version: '1.0.1',
      size: '90.7 MB',
      updateDate: '2024-12-18',
      color: 'from-amber-500 to-yellow-500',
      features: ['自动更新', '访问稳定', '高性能']
    },
    {
      id: 'macos',
      name: 'MacOS',
      icon: <SiMacos />,
      description: '为 Mac 用户打造的流畅体验',
      downloadUrl: 'https://juliawl.oss-cn-shanghai.aliyuncs.com/ai/down/ChatGPT%20PLUS-1.0.1.dmg',
      version: '1.0.1',
      size: '133.8 MB',
      updateDate: '2024-12-20',
      color: 'from-orange-400 to-rose-500',
      features: ['原生设计', 'Touch Bar', '访问稳定']
    }
  ];

  const handleDownload = (platform, url) => {
    if (isWeChatBrowser()) {
      setShowWeChatTip(true);
      return;
    }

    // 先触发下载
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 然后显示安装指南
    if (platform === 'ios') {
      setShowIOSGuide(true);
    } else if (platform === 'macos') {
      setShowMacGuide(true);
    }
  };

  return (
    <div className="download-page">
      {/* 动态背景 */}
      <div className="background-container">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      {/* Hero Section */}
      <motion.section 
        className="hero-section"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div 
          className="hero-badge"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <span className="badge-icon">✨</span>
          <span>AI 驱动的智能助手</span>
        </motion.div>

        <motion.h1 
          className="hero-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <span className="gradient-text">Julian AIGPT PRO</span>
          <br />
          一站式使用全球最先进AI大模型
        </motion.h1>

        <motion.p 
          className="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          支持ChatGPT、GPT-5、Claude等10+主流AI · 稳定直连 · 无需梯子 · 企业级服务保障
        </motion.p>

        {/* 特性标签 */}
        <motion.div 
          className="feature-tags"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <div className="feature-tag">
            <FaRocket />
            <span>毫秒级响应</span>
          </div>
          <div className="feature-tag">
            <FaShieldAlt />
            <span>无需梯子</span>
          </div>
          <div className="feature-tag">
            <FaUsers />
            <span>80000+ 用户</span>
          </div>
          <div className="feature-tag">
            <FaCheckCircle />
            <span>2年稳定运行</span>
          </div>
          <div className="feature-tag">
            <FaCheckCircle />
            <span>支持开发票</span>
          </div>
        </motion.div>

        {/* 域名更换公告横幅 */}
        <motion.div
          className="notice-banner"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <div className="notice-icon">
            <FaExclamationTriangle />
          </div>
          <div className="notice-content">
            <strong>重要通知：</strong>因不可抗力因素，<span className="highlight-domain">chataiii.com</span> 域名国内已无法访问，如果您使用的是这个地址请及时更换
          </div>
        </motion.div>
      </motion.section>

      {/* 网页访问区域 - 移到最上面 */}
      <motion.section
        className="web-access-section"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <div className="web-access-card">
          <div className="web-access-content">
            <div className="web-icon-wrapper">
              <FaGlobe className="web-icon" />
            </div>
            <div className="web-text">
              <h3>无需下载，即刻体验</h3>
              <p>支持网页端访问，随时随地使用所有AI模型</p>
            </div>
          </div>
          <motion.a
            href="https://url.julianaigpt.com"
            target="_blank"
            rel="noopener noreferrer"
            className="web-access-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaGlobe />
            <span>访问网页版</span>
            <div className="btn-shine"></div>
          </motion.a>
        </div>
      </motion.section>

      {/* 下载卡片网格 - 移到第二位 */}
      {showDownloads ? (
        <section className="download-section">
          <div className="section-header">
            <h2 className="section-title">客户端下载</h2>
            <p className="section-subtitle">选择适合你的平台，获得最佳体验</p>
          </div>
          <div className="download-grid">
          {platforms.map((platform, index) => (
            <motion.div
              key={platform.id}
              className={`platform-card ${platform.disabled ? 'disabled' : ''} ${hoveredCard === platform.id ? 'hovered' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
              onMouseEnter={() => setHoveredCard(platform.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="card-glow"></div>
              
              <div className="card-header">
                <div className={`platform-icon-wrapper ${platform.color}`}>
                  <div className="icon-inner">
                    {platform.icon}
                  </div>
                </div>
                <div className="platform-details">
                  <h3 className="platform-name">{platform.name}</h3>
                  <p className="platform-desc">{platform.description}</p>
                </div>
              </div>

              <div className="card-features">
                {platform.features.map((feature, idx) => (
                  <div key={idx} className="feature-item">
                    <span className="feature-dot"></span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div className="card-info">
                <div className="info-group">
                  <span className="info-label">版本</span>
                  <span className="info-value">{platform.version}</span>
                </div>
                <div className="info-group">
                  <span className="info-label">大小</span>
                  <span className="info-value">{platform.size}</span>
                </div>
                <div className="info-group">
                  <span className="info-label">更新</span>
                  <span className="info-value">{platform.updateDate.split('-')[1]}/{platform.updateDate.split('-')[2]}</span>
                </div>
              </div>

              <motion.button
                className={`download-btn ${platform.disabled ? 'disabled' : ''}`}
                onClick={(e) => {
                  if (!platform.disabled) {
                    e.preventDefault();
                    handleDownload(platform.id, platform.downloadUrl);
                  }
                }}
                whileHover={platform.disabled ? {} : { scale: 1.02 }}
                whileTap={platform.disabled ? {} : { scale: 0.98 }}
              >
                <FaDownload />
                <span>{platform.disabled ? platform.disabledText : '立即下载'}</span>
              </motion.button>
            </motion.div>
          ))}
          </div>
        </section>
      ) : (
        /* 对爬虫显示的替代内容 */
        <section className="download-section">
          <div className="section-header">
            <h2 className="section-title">多平台支持</h2>
            <p className="section-subtitle">支持 Windows、MacOS、iOS、Android 全平台访问</p>
          </div>
          <div className="platform-features">
            <div className="feature-highlight">
              <FaGlobe className="feature-icon" />
              <h3>网页版</h3>
              <p>无需下载，浏览器直接访问</p>
            </div>
            <div className="feature-highlight">
              <FaRocket className="feature-icon" />
              <h3>高性能</h3>
              <p>毫秒级响应，极速体验</p>
            </div>
            <div className="feature-highlight">
              <FaShieldAlt className="feature-icon" />
              <h3>安全稳定</h3>
              <p>企业级安全保障</p>
            </div>
          </div>
        </section>
      )}

      {/* AI模型展示区 - 移到最后 */}
      <motion.section 
        className="models-section"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.8 }}
      >
        <div className="section-header">
          <h2 className="section-title">支持的 AI 模型</h2>
          <p className="section-subtitle">10大顶尖AI模型，包含ChatGPT、Claude、Gemini等，全部免费使用</p>
        </div>

        <div className="models-grid">
          {aiModels.map((model, index) => (
            <motion.div
              key={model.id}
              className={`model-card ${hoveredModel === model.id ? 'hovered' : ''}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.0 + index * 0.05, duration: 0.4 }}
              onMouseEnter={() => setHoveredModel(model.id)}
              onMouseLeave={() => setHoveredModel(null)}
            >
              <div className="model-glow"></div>
              <div className={`model-icon ${model.color}`}>
                {model.icon}
              </div>
              <h3 className="model-name">{model.name}</h3>
              <p className="model-desc">{model.description}</p>
              <div className="model-features">
                {model.features.map((feature, idx) => (
                  <span key={idx} className="model-feature-tag">{feature}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* 信任指标 */}
      <motion.section 
        className="trust-section"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
      >
        <div className="trust-stats">
          <div className="stat-item">
            <div className="stat-number">99.9%</div>
            <div className="stat-label">服务可用性</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">&lt;100ms</div>
            <div className="stat-label">平均响应时间</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">全天候支持</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">全球</div>
            <div className="stat-label">数据节点覆盖</div>
          </div>
        </div>
      </motion.section>

      {/* 弹窗组件 */}
      <AnimatePresence>
        {showIOSGuide && <IOSGuide onClose={() => setShowIOSGuide(false)} />}
        {showMacGuide && <MacGuide onClose={() => setShowMacGuide(false)} />}
        {showWeChatTip && <WeChatTip onClose={() => setShowWeChatTip(false)} />}
      </AnimatePresence>
    </div>
  );
};

export default DownloadPage;

